import {
  CalendarOutlined,
  DeleteOutlined,
  DownOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LinkOutlined,
  PaperClipOutlined,
  TeamOutlined,
  VideoCameraOutlined
} from "@ant-design/icons"
import {
  Button,
  Checkbox,
  Form,
  Popover,
  Select,
  Typography,
  type UploadProps
} from "antd"
import { type UploadFile } from "antd/lib"
import { type Store } from "antd/lib/form/interface"
import { type BaseOptionType } from "antd/lib/select"
import { type UploadChangeParam } from "antd/lib/upload"
import dayjs from "dayjs"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { type RootState } from "../../../../../store.ts"
import { AddressAutoComplete } from "../../../../../components/AutoComplete/AddressAutoComplete"
import { AttachmentsList } from "../../../../../components/AttachmentsList"
import { TextArea } from "../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"
import { CustomDatePicker } from "../../../../../components/Date&TimePickers/CustomDatePicker"
import { CustomTimePicker } from "../../../../../components/Date&TimePickers/CustomTimePicker"
import { FileUpload } from "../../../../../components/FileUpload"
import { DATE_FORMAT } from "../../../../../utils/date/date"
import { getDateSafe } from "../../../../../utils/date/get-date-safe"
import { validateFile } from "../../../../../utils/file-validation"
import { useCalendarNotificationPreferences } from "../../../helpers/use-calendar-notification-preferences.ts"
import {
  APPOINTMENT_DEFAULT_MINUTES,
  FOLLOW_UP_DEFAULT_MINUTES
} from "../../../../Lead/LeadWorkspacePage/schedule/schedule-durations.ts"
import type { CalendarAttachment, Event } from "../../../types/types"
import { CalendarAddAppointmentTypeSelect } from "./CalendarAddAppointmentTypeSelect"
import { CalendarAddExternalParticipantsFormItem } from "./CalendarAddExternalParticipantsFormItem"
import { CalendarAddLeadsSelect } from "./CalendarAddLeadsSelect"
import { CalendarAddParticipantsSelect } from "./CalendarAddParticipantsSelect"
import { CalendarAddProjectsSelect } from "./CalendarAddProjectsSelect"
import { CalendarAddReminderOffsetField } from "./CalendarAddReminderOffsetField.tsx"
import { CalendarColorPicker } from "./CalendarColorPicker"
import { CalendarFormScrollArea } from "./CalendarFormScrollArea"
import { CalendarFormSection } from "./CalendarFormSection.tsx"
import { ProjectEducationInviteSection } from "./ProjectEducationInviteSection.tsx"

dayjs.extend(isSameOrBefore)

const { Text } = Typography

const optionValue = (field: unknown) =>
  field && typeof field === "object"
    ? (field as BaseOptionType).value
    : (field as string | undefined)

/** The address the picked record suggests, carried on the option. */
const suggestedAddressOf = (option: unknown) =>
  (option as { suggestedAddress?: string } | undefined)?.suggestedAddress ?? ""

// A header action, not a form field: sized to its own text (never a fixed
// or stretched box), 36px tall, subtle neutral chrome.
const HEADER_CHIP_CLASS =
  "flex h-9 w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm font-medium text-gray-600 hover:bg-gray-100"

const MAX_SIZE_MB = 200
const MAX_FILE_COUNT = 20
const ALLOWED_FILE_EXTENSIONS = [
  "png",
  "jpeg",
  "jpg",
  "pdf",
  "mp4",
  "mov",
  "webm"
  // "doc",
  // "docx",
  // "txt"
]

interface CalendarAddEventFormProps {
  loading: boolean
  isEdit?: boolean
  timezone: string
  // A caller that returns a promise rejecting with the error envelope gets a
  // `share_project_education` field error shown under that checkbox.
  onSubmit: (event: CalendarEventFormSubmission) => void | Promise<unknown>

  onClose: () => void
  // Present only when editing and the user may delete — the Quick View's own
  // confirm-then-dispatch handler, reused as-is rather than duplicated here.
  onDelete?: () => void
  initialValues: Store | undefined
  lockedLead?: BaseOptionType
  submitLabel?: string
  additionalFields?: React.ReactNode
  /** The linked record's customer email, when the caller knows it. Offered in
   *  the invitation field once "Invite Customer" is ticked, and left alone
   *  after that so the user can edit or clear it. */
  customerEmail?: string
  /** Resolved once by the caller (Company HQ's Branches filter, or a single
   *  branch workspace's active branch) — the branches a Branch field would
   *  offer if it needs to appear at all. Left empty by every caller that
   *  can't produce an ambiguous branch anyway (editing an existing event,
   *  or creating one already scoped to a Lead), so this component itself
   *  stays free of any redux dependency. */
  branchCandidates?: { id: string; name: string }[]
}

interface CalendarEventFormSubmission extends Record<string, unknown> {
  title: string
  type: "EVENT"
  all_day: boolean
  date: string
  start_at: string
  end_at: string
  lead?: string
  project?: string
  participants?: string[]
}

export function CalendarAddEventForm({
  initialValues,
  onClose,
  onSubmit,
  onDelete,
  timezone,
  isEdit = false,
  loading,
  lockedLead,
  submitLabel,
  additionalFields,
  customerEmail,
  branchCandidates = []
}: CalendarAddEventFormProps) {
  const { t } = useTranslation()
  const [eventForm] = Form.useForm()
  const [notificationPreferences] = useCalendarNotificationPreferences()
  const category = Form.useWatch("category", eventForm)
  const inviteCustomer = Form.useWatch("invite_customer", eventForm)
  const leadValue = Form.useWatch("lead", eventForm)
  const customerUploadChecked = Form.useWatch(
    "customer_upload_enabled",
    eventForm
  )
  // Uploads land in a lead's files, so the option exists only on a lead meeting.
  const leadId = optionValue(lockedLead) ?? optionValue(leadValue)
  const hasLead = Boolean(leadId)
  const showCustomerUpload = Boolean(inviteCustomer) && hasLead
  const uploadWillReinvite =
    isEdit &&
    Boolean(initialValues?.invite_customer) &&
    !initialValues?.customer_upload_enabled &&
    Boolean(customerUploadChecked)
  // `state.auth` is optional-chained: several suites mount this form against
  // stores with no `auth` slice.
  const featureProjectEducation = useSelector(
    (state: RootState) => !!state.auth?.userInfo?.feature_project_education
  )
  const featureProjectEducationCustomerSharing = useSelector(
    (state: RootState) =>
      !!state.auth?.userInfo?.feature_project_education_customer_sharing
  )
  const showProjectEducation = hasLead && featureProjectEducation
  const allDay = Form.useWatch("all_day", eventForm)
  const customColorValue = Form.useWatch("custom_color", eventForm)
  // A follow-up is a reminder to act, not a meeting: it takes a moment in the
  // day, so it asks for one time and books the same block a reminder does.
  const isFollowUp = category === "FOLLOW_UP"

  // Related Records visibility follows the appointment type, not which field
  // the user happened to fill in first: a lead meeting only ever links a
  // lead, a project meeting only ever links a project, and every other type
  // (site visit, inspection, proposal, personal, follow-up...) can go either
  // way, so both stay offered. `lockedLead` overrides this — the form was
  // opened scoped to a specific lead (e.g. from that Lead's own page), which
  // outranks whatever type gets picked.
  const isLeadOnlyType = category === "LEAD_MEETING"
  const isProjectOnlyType = category === "PROJECT_MEETING"
  const showLeadField = Boolean(lockedLead) || !isProjectOnlyType
  const showProjectField = !lockedLead && !isLeadOnlyType
  // A form scoped to one lead (every Lead Edit usage) only ever shows this
  // one field — it should take the full row rather than sit squeezed into
  // half a two-column grid meant for the lead+project pair.
  const showBothLinkFields = showLeadField && showProjectField

  // Branch: the backend derives it silently from a linked Lead/Project
  // (see `CalendarEventSerializer._resolve_team`), so this field only ever
  // needs to appear when neither is linked AND which branch is ambiguous —
  // never when there's only one candidate, since the server already
  // defaults to it without being asked. `branchCandidates` already reflects
  // that scope (Company HQ's Branches filter, or a single branch
  // workspace's active branch) — resolved once by the caller, not here.
  const projectValue = Form.useWatch("project", eventForm)
  const showBranchField =
    !optionValue(leadValue) &&
    !optionValue(projectValue) &&
    branchCandidates.length > 1

  // Clears only the field the *new* type just hid — not on mount (an edited
  // event must still load whatever it already has), only on an actual
  // in-session type change, so switching types never leaves a stale lead or
  // project silently riding along on a type that no longer shows it.
  const previousCategoryRef = useRef(category)
  useEffect(() => {
    const previousCategory = previousCategoryRef.current
    previousCategoryRef.current = category
    if (previousCategory === category) return

    if (category === "PROJECT_MEETING") {
      eventForm.setFieldValue("lead", undefined)
    } else if (category === "LEAD_MEETING") {
      eventForm.setFieldValue("project", undefined)
    }
  }, [category, eventForm])

  // Whether the user has picked End Time/End Date themselves *in this
  // session* — not inferred from whether the field merely has a value,
  // since an auto-suggested value is indistinguishable from a manual one by
  // value alone. Only these fields' own onChange sets them; the auto-follow
  // logic below never does, since it goes through setFieldValue directly.
  const endTimeManuallySetRef = useRef(false)
  const endDateManuallySetRef = useRef(false)

  // End Time's `dependencies` (below) re-run its rules — required rule
  // included — the instant Start Date/Start Time/End Date change, even
  // before the user has ever looked at End Time (rc-field-form validates
  // every dependent field on a dependency change, regardless of touch
  // state). Gating the required message on an actual submit attempt is
  // what keeps it from flashing red the moment Start Date is picked.
  const hasAttemptedSubmitRef = useRef(false)

  // The gap between Start and End, preserved while End hasn't been manually
  // picked — a fresh form defaults to the standard appointment length/a
  // same-day span; editing an existing event starts from whatever gap it
  // already has, so nudging Start preserves that meeting's real duration
  // instead of silently shortening or extending it. Gated on `isEdit`, not
  // just on both values being present — clicking an empty calendar slot
  // (resolveNewEventSlot) also pre-fills both start_at/end_at, 30 minutes
  // apart, which isn't a real gap to preserve; without the `isEdit` check
  // every slot-created event locked onto that 30-minute pre-fill instead of
  // the documented 60-minute standard appointment length.
  const initialStartAt = getDateSafe(initialValues?.start_at)
  const initialEndAt = getDateSafe(initialValues?.end_at)
  const endTimeOffsetMinutesRef = useRef<number>(
    isEdit && initialStartAt && initialEndAt
      ? Math.max(initialEndAt.diff(initialStartAt, "minute"), 15)
      : APPOINTMENT_DEFAULT_MINUTES
  )

  const initialStartDate = getDateSafe(
    initialValues?.start_date ?? initialValues?.date
  )
  const initialEndDate = getDateSafe(
    initialValues?.end_date ?? initialValues?.date
  )
  const endDateOffsetDaysRef = useRef<number>(
    initialStartDate && initialEndDate
      ? Math.max(initialEndDate.diff(initialStartDate, "day"), 0)
      : 0
  )

  const [uploadedFiles, setUploadedFiles] = useState<UploadFile<any>[]>([])
  const [deletedFiles, setDeletedFiles] = useState<string[]>([])

  // What this form last put in Location itself. Anything else in the field is
  // the user's, and picking a different record must not overwrite it — that is
  // the one mistake here they cannot undo.
  const offeredAddress = useRef<string>("")

  const offerAddress = (option: unknown) => {
    const suggested = suggestedAddressOf(option)
    const current = (eventForm.getFieldValue("address") ?? "").trim()

    if (current && current !== offeredAddress.current) return

    eventForm.setFieldValue("address", suggested || undefined)
    offeredAddress.current = suggested
  }

  // Opened from a lead, nothing is ever picked: the lead arrives already
  // chosen and its control is locked, so the picker's onChange — the only
  // thing that offered an address — never fires. Offer it once on mount
  // instead. Editing is left alone: that event's Location is already its own.
  const lockedOption = lockedLead ?? initialValues?.lead
  useEffect(() => {
    if (isEdit) return
    offerAddress(lockedOption)
  }, [isEdit, lockedOption])

  // Once the user opts to invite the customer, show the email address they'll
  // get so it can be checked and changed before sending. Only ever fills a
  // blank field, so a value the event carried or the user typed always wins.
  useEffect(() => {
    if (!inviteCustomer || !customerEmail) return
    const current = (
      eventForm.getFieldValue("customer_invite_email") ?? ""
    ).trim()
    if (current) return
    eventForm.setFieldValue("customer_invite_email", customerEmail)
  }, [inviteCustomer, customerEmail])

  const attachments = initialValues?.attachments?.filter(
    (file: CalendarAttachment) => !deletedFiles?.includes(file?.id)
  )

  const handleModalCancel = () => {
    eventForm.resetFields()
    onClose()
  }

  const normalizeEmail = (email?: string | null) => email?.toLowerCase() || ""

  // `invite_customer` is a request to send, not a state flag: the backend mails
  // the customer every time it arrives true. Re-asserting the invite the event
  // already carries would mail them again on every unrelated edit.
  const requestsCustomerInvite = (
    inviteCustomerChecked?: boolean,
    email?: string | null
  ) => {
    if (!inviteCustomerChecked) return false
    if (!isEdit) return true

    const alreadyInvited = normalizeEmail(initialValues?.customer_invite_email)
    if (!alreadyInvited) return true

    return normalizeEmail(email) !== alreadyInvited
  }

  // The box was ticked when the form opened and is now clear: the invitation
  // has to be withdrawn. `invite_customer: false` only carries that meaning
  // when it was on before — otherwise it is the untouched default the backend
  // ignores, so it is sent only on this transition.
  const withdrawsCustomerInvite = (inviteCustomerChecked?: boolean) =>
    isEdit && !inviteCustomerChecked && !!initialValues?.invite_customer

  // The `share_project_education` value to send, or `undefined` to leave it
  // out. Uninviting the customer always switches sharing off. A hidden
  // checkbox (the sharing flag off, or no right to share) is not a "no":
  // its field is simply absent, and the stored value stays. An edit sends
  // the key only when the value changes.
  const shareProjectEducationToSend = (
    inviteCustomerChecked?: boolean,
    shareChecked?: boolean
  ): boolean | undefined => {
    let nextValue: boolean
    if (!inviteCustomerChecked) nextValue = false
    else if (shareChecked === undefined) return undefined
    else nextValue = !!shareChecked
    if (isEdit && nextValue === !!initialValues?.share_project_education)
      return undefined
    return nextValue
  }

  // The backend's exception handler lifts the field's message to
  // `details.message` as well as `details.field_errors`.
  const shareProjectEducationErrorMessage = (
    error: unknown
  ): string | undefined => {
    const details = (
      error as {
        details?: { message?: string; field_errors?: Record<string, string[]> }
      }
    )?.details
    if (!details?.field_errors?.share_project_education) return undefined
    return details.message || details.field_errors.share_project_education[0]
  }

  const handleEventSubmit = async ({
    upload,
    all_day,
    start_at,
    end_at,
    start_date,
    end_date,
    lead,
    project,
    participants,
    invite_customer,
    customer_invite_email,
    customer_upload_enabled,
    share_project_education,
    ...values
  }: any) => {
    const startDay = dayjs(start_date)
    // A follow-up is one moment, not a range: its own end is always derived
    // from the start (see FOLLOW_UP_DEFAULT_MINUTES below), so End Date is
    // never shown for it and must not be read here.
    const endDay = isFollowUp ? startDay : dayjs(end_date)

    // All day: the wire contract other all-day rows already use — UTC
    // midnight of the start day through end-of-day UTC of the end day — per
    // docs/kb/decisions/2026-08-20-all-day-is-read-through-one-shared-reader.md.
    // A single-day all-day event is simply start_date === end_date; a
    // multi-day one (Start Date < End Date) renders as one continuous block
    // because FullCalendar already spans any all-day event across however
    // many day columns its start/end cover — nothing extra was needed there.
    // Timed: Start/End Date and Start/End Time are now independent fields —
    // a timed event can span multiple days the same way an all-day one can.
    const start = all_day
      ? dayjs.utc(startDay.format(DATE_FORMAT))
      : dayjs.tz(
          `${startDay.format(DATE_FORMAT)} ${start_at.format("HH:mm")}`,
          "YYYY-MM-DD HH:mm",
          timezone
        )

    const end = all_day
      ? dayjs.utc(endDay.format(DATE_FORMAT)).endOf("day")
      : isFollowUp
        ? start.add(FOLLOW_UP_DEFAULT_MINUTES, "minute")
        : dayjs.tz(
            `${endDay.format(DATE_FORMAT)} ${end_at.format("HH:mm")}`,
            "YYYY-MM-DD HH:mm",
            timezone
          )

    const shareProjectEducation = shareProjectEducationToSend(
      invite_customer,
      share_project_education
    )

    const params: CalendarEventFormSubmission = {
      type: "EVENT",
      all_day: !!all_day,
      date: startDay.format(DATE_FORMAT),
      start_at: start.utc().format(),
      end_at: end.utc().format(),
      participants: participants?.map((p: any) => p?.value || p),
      file_list: upload?.map((file: any) => ({ file_type: 5, file })) || [],
      lead: optionValue(lead),
      project: optionValue(project),
      ...(deletedFiles?.length && { deleted_attachment_ids: deletedFiles }),
      ...values,
      ...(requestsCustomerInvite(invite_customer, customer_invite_email) && {
        invite_customer: true,
        ...(customer_invite_email && { customer_invite_email })
      }),
      ...(withdrawsCustomerInvite(invite_customer) && {
        invite_customer: false
      }),
      // A state, not a request: always sent for a lead meeting, and only on for
      // as long as the customer is invited at all.
      ...(optionValue(lead) && {
        customer_upload_enabled: !!invite_customer && !!customer_upload_enabled
      }),
      ...(optionValue(lead) &&
        shareProjectEducation !== undefined && {
          share_project_education: shareProjectEducation
        })
    }

    try {
      await onSubmit?.(params)
    } catch (error) {
      const shareMessage = shareProjectEducationErrorMessage(error)
      if (shareMessage) {
        eventForm.setFields([
          { name: "share_project_education", errors: [shareMessage] }
        ])
        return
      }
      // Any other rejection was already reported by the caller.
    }
  }

  // Keeps End Date the same distance from Start Date it already was (zero
  // days — same-day — for a fresh form) until the user picks End Date
  // themselves. `setFieldValue` never runs the picker's own onChange, so it
  // can't be mistaken for a manual pick, and the explicit `validateFields`
  // call is what actually clears a stale "required"/range error the moment a
  // valid value lands — `setFieldValue` alone does not re-run validation.
  const onStartDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) return
    if (endDateManuallySetRef.current) return

    eventForm.setFieldValue(
      "end_date",
      value.add(endDateOffsetDaysRef.current, "day")
    )
    eventForm.validateFields(["end_date"])
  }

  const beforeUpload = (file: any) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS
    })
  }

  const uploadFileChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setUploadedFiles(info.fileList)
  }

  const onDeletedFile = (file: CalendarAttachment) => {
    setDeletedFiles((prev) => [...prev, file?.id])
  }

  const availableCountFiles =
    MAX_FILE_COUNT - (initialValues?.attachments?.length || 0)

  const uploadProps: UploadProps = {
    multiple: true,
    accept: ALLOWED_FILE_EXTENSIONS?.map((ext) => `.${ext}`).join(","),
    maxCount: availableCountFiles,
    beforeUpload,
    onDrop: (e) => {
      const files = Array.from(e?.dataTransfer?.files)

      files.forEach((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase()
        if (!ALLOWED_FILE_EXTENSIONS.includes(ext || "")) {
          files.forEach(beforeUpload)
        }
      })
    },
    onChange: uploadFileChange
  }

  return (
    <Form
      form={eventForm}
      layout="vertical"
      className={"with-default-error flex flex-col flex-1 overflow-hidden"}
      onValuesChange={(changed) => {
        // antd keeps a hidden field's value; unticking Invite Customer must
        // clear the upload and share boxes too, or re-ticking brings them
        // back ticked.
        if ("invite_customer" in changed && !changed.invite_customer) {
          eventForm.setFieldValue("customer_upload_enabled", false)
          eventForm.setFieldValue("share_project_education", false)
        }
      }}
      onFinish={handleEventSubmit}
      // Capture phase so this always runs before rc-field-form's own submit
      // handling validates the fields below — covers Enter-key submission
      // from any input, not just a click on the Save button, which a plain
      // onClick on the button would miss entirely.
      onSubmitCapture={() => {
        hasAttemptedSubmitRef.current = true
      }}
      validateTrigger={["onSubmit", "onChange"]}
      initialValues={{
        invite_customer: false,
        customer_upload_enabled: false,
        share_project_education: false,
        all_day: false,
        // A brand-new event defaults to Calendar Settings' configured
        // reminder (5 minutes until a user changes it there — see
        // calendar-notification-preferences.ts); an edited one keeps
        // whatever it already has (including an explicit None/null) — never
        // injected once `initialValues` carries a real event, so this can't
        // silently migrate an existing event's saved choice.
        ...(isEdit
          ? {}
          : { reminder_offset: notificationPreferences.defaultReminderOffset }),
        ...initialValues,
        ...(lockedLead ? { lead: lockedLead } : {}),
        start_at: getDateSafe(initialValues?.start_at),
        end_at: getDateSafe(initialValues?.end_at),
        // Start/End Date now drive both the all-day and the timed layout, so
        // both need a value however the form was opened: a slot click only
        // ever set `date` (see resolveNewEventSlot), Edit sets start_date/
        // end_date directly from the event's real range (see CalendarView.tsx).
        start_date: getDateSafe(
          initialValues?.start_date ?? initialValues?.date
        ),
        end_date: getDateSafe(initialValues?.end_date ?? initialValues?.date)
      }}
      // onKeyDown={(e) => e.key === "Enter" && eventForm.submit()}
    >
      {/* Phase 1: Event color and Reminder moved here from the form body —
          one row of compact header-action chips, each sized to its own
          content (never a fixed/stretched width), not form fields. Both
          triggers share one button style so Reminder doesn't read as a
          nested antd Select-in-a-box. Busy/Show-as and Private event are
          intentionally NOT added here: the client's mockup shows them, but
          CalendarEvent has no backing field for either (verified against
          ezbuild_back), and this phase is scoped to only the two controls
          that already have somewhere real to persist. */}
      <div className={"mb-4 flex shrink-0 items-center gap-2"}>
        <Popover
          trigger={"click"}
          placement={"bottomLeft"}
          forceRender
          content={
            <div className={"max-w-64"}>
              <p className={"mb-2 text-xs text-gray-400"}>
                Leave unset to use the event type&apos;s usual color.
              </p>
              <Form.Item name="custom_color" className={"mb-0 min-w-0"} noStyle>
                <CalendarColorPicker />
              </Form.Item>
            </div>
          }
        >
          <button type={"button"} className={HEADER_CHIP_CLASS}>
            <span
              className={"size-2.5 shrink-0 rounded-full"}
              style={{ backgroundColor: customColorValue || "#9CA3AF" }}
            />
            Event color
            <DownOutlined className={"text-[10px]"} />
          </button>
        </Popover>

        {/* No wrapping Popover: CalendarAddReminderOffsetField is a Dropdown
            over the real option list directly, so one click on the chip
            opens the actual choices — no second, nested popup to open. */}
        <Form.Item name="reminder_offset" className={"mb-0 min-w-0"} noStyle>
          <CalendarAddReminderOffsetField
            className={HEADER_CHIP_CLASS}
            promptOnMountIfSet={!isEdit}
          />
        </Form.Item>
      </div>

      <CalendarFormScrollArea>
        <CalendarFormSection
          icon={<FileTextOutlined />}
          title={"Basic Information"}
        >
          <div className={"grid grid-cols-1 gap-5 sm:grid-cols-2"}>
            <Form.Item
              label="Title"
              name="title"
              className={"mb-0 min-w-0"}
              rules={[
                { required: true, message: "Please enter event title" },
                { min: 3, message: "Title must be at least 3 characters long" }
              ]}
            >
              <TextInput placeholder="Event title" size="large" />
            </Form.Item>
            <Form.Item
              label={t("appointmentType.label")}
              name="category"
              className={"mb-0 min-w-0"}
              validateTrigger={["onSubmit", "onChange", "onBlur"]}
              rules={[
                { required: true, message: t("appointmentType.required") }
              ]}
            >
              <CalendarAddAppointmentTypeSelect />
            </Form.Item>
          </div>
          {additionalFields}
        </CalendarFormSection>

        <CalendarFormSection icon={<CalendarOutlined />} title={"Date & Time"}>
          <Form.Item
            name="all_day"
            valuePropName="checked"
            className={"mb-0 min-w-0"}
          >
            <Checkbox>
              All day{" "}
              <Text type={"secondary"} className={"font-normal"}>
                — Event lasts the entire day or multiple days
              </Text>
            </Checkbox>
          </Form.Item>

          <div className={"flex flex-col gap-4"}>
            <div>
              <Text
                strong
                className={
                  "mb-2 block text-xs uppercase tracking-wide text-gray-500"
                }
              >
                Start
              </Text>
              <div
                className={`grid grid-cols-1 gap-5 ${allDay ? "" : "sm:grid-cols-2"}`}
              >
                <Form.Item
                  label="Start Date"
                  name="start_date"
                  className={"mb-0 min-w-0"}
                  rules={[
                    { required: true, message: "Please select a start date" }
                  ]}
                >
                  <CustomDatePicker
                    className="w-full"
                    size="large"
                    onChange={onStartDateChange}
                  />
                </Form.Item>

                {!allDay && (
                  <Form.Item
                    label="Start Time"
                    name="start_at"
                    className={"mb-0 min-w-0"}
                    rules={[
                      {
                        required: true,
                        message: "Please select start time"
                      }
                    ]}
                  >
                    <CustomTimePicker
                      className="w-full"
                      size="large"
                      needConfirm={false}
                      onChange={(startTime) => {
                        if (!startTime) return

                        // Once the user has picked End Time themselves, Start
                        // moving is not license to move it again — only
                        // re-check it against the new Start, never overwrite it.
                        if (endTimeManuallySetRef.current) {
                          eventForm.validateFields(["end_at"])
                          return
                        }

                        const suggested = dayjs(startTime).add(
                          endTimeOffsetMinutesRef.current,
                          "minute"
                        )

                        // The picker stops at 23:45 while the form books one day, so
                        // an hour later can fall off the end of it.
                        if (!suggested.isSame(dayjs(startTime), "day")) return

                        // The scheduled day may not contain that wall clock time at
                        // all. Submit re-parses HH:mm in the account's zone, and a
                        // spring-forward gap resolves 02:30 to 03:30 — saving an hour
                        // the user never picked. Ask the same question submit will.
                        const hourAndMinute = suggested.format("HH:mm")
                        const scheduled = eventForm.getFieldValue("start_date")
                        const onScheduledDay = dayjs.tz(
                          `${dayjs(scheduled ?? startTime).format(
                            DATE_FORMAT
                          )} ${hourAndMinute}`,
                          "YYYY-MM-DD HH:mm",
                          timezone
                        )
                        if (onScheduledDay.format("HH:mm") !== hourAndMinute)
                          return

                        // `setFieldValue` never runs the picker's own onChange
                        // (that's also why it can't be mistaken for a manual
                        // pick above), so it doesn't re-run validation either
                        // — without the explicit call, End Time keeps showing
                        // its stale "required" error under the value that
                        // just made it valid.
                        eventForm.setFieldValue("end_at", suggested)
                        eventForm.validateFields(["end_at"])
                      }}
                    />
                  </Form.Item>
                )}
              </div>
            </div>

            {!isFollowUp && (
              <div>
                <Text
                  strong
                  className={
                    "mb-2 block text-xs uppercase tracking-wide text-gray-500"
                  }
                >
                  End
                </Text>
                <div
                  className={`grid grid-cols-1 gap-5 ${allDay ? "" : "sm:grid-cols-2"}`}
                >
                  <Form.Item
                    label="End Date"
                    name="end_date"
                    className={"mb-0 min-w-0"}
                    dependencies={["start_date"]}
                    rules={[
                      { required: true, message: "Please select an end date" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve()
                          const start = eventForm.getFieldValue("start_date")
                          if (!start) return Promise.resolve()

                          if (dayjs(value).isBefore(dayjs(start), "day")) {
                            return Promise.reject(
                              new Error(
                                "End date must be on or after the start date"
                              )
                            )
                          }

                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <CustomDatePicker
                      className="w-full"
                      size="large"
                      // Only a real pick here — never the auto-follow's own
                      // `setFieldValue` — marks End Date as user-controlled,
                      // per the same session-scoped tracking End Time uses.
                      // A clear (antd's own clear button fires onChange(null))
                      // resets it back to false rather than leaving it stuck
                      // true forever — otherwise clearing End Date meant no
                      // later Start Date change could ever refill it again.
                      onChange={(value) => {
                        endDateManuallySetRef.current = !!value
                      }}
                    />
                  </Form.Item>

                  {!allDay && (
                    <Form.Item
                      label="End Time"
                      name="end_at"
                      className={"mb-0 min-w-0"}
                      dependencies={["start_date", "start_at", "end_date"]}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value) return Promise.resolve()
                            if (!hasAttemptedSubmitRef.current) {
                              return Promise.resolve()
                            }
                            return Promise.reject(
                              new Error("Please select end time")
                            )
                          }
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve()
                            const startTime =
                              eventForm.getFieldValue("start_at")
                            const startDate =
                              eventForm.getFieldValue("start_date")
                            const endDate = eventForm.getFieldValue("end_date")
                            if (!startTime || !startDate || !endDate) {
                              return Promise.resolve()
                            }

                            const startDayjs = dayjs(
                              `${dayjs(startDate).format(DATE_FORMAT)} ${dayjs(startTime).format("HH:mm")}`
                            )
                            const endDayjs = dayjs(
                              `${dayjs(endDate).format(DATE_FORMAT)} ${dayjs(value).format("HH:mm")}`
                            )

                            if (endDayjs.isSameOrBefore(startDayjs)) {
                              return Promise.reject(
                                new Error(
                                  "End date/time must be after the start date/time"
                                )
                              )
                            }

                            // A minimum slot length only makes sense within a
                            // single day — any multi-day gap already clears it.
                            if (startDayjs.isSame(endDayjs, "day")) {
                              const diffInMinutes = endDayjs.diff(
                                startDayjs,
                                "minute"
                              )

                              if (diffInMinutes < 15) {
                                return Promise.reject(
                                  new Error(
                                    "At least 15 minutes after the start time"
                                  )
                                )
                              }
                            }

                            return Promise.resolve()
                          }
                        }
                      ]}
                    >
                      <CustomTimePicker
                        className="w-full"
                        size="large"
                        needConfirm={false}
                        // Only a real pick here — never the auto-follow's own
                        // `setFieldValue` — marks End Time as user-controlled.
                        // A clear resets it back to false (see End Date's own
                        // onChange above for why).
                        onChange={(value) => {
                          endTimeManuallySetRef.current = !!value
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
            )}
          </div>
        </CalendarFormSection>

        <CalendarFormSection icon={<LinkOutlined />} title={"Related Records"}>
          <div className={"grid grid-cols-1 gap-5 sm:grid-cols-2"}>
            {showLeadField && (
              <Form.Item
                label="Link to Lead"
                name="lead"
                className={`mb-0 min-w-0 ${showBothLinkFields ? "" : "sm:col-span-2"}`}
              >
                <CalendarAddLeadsSelect
                  disabled={!!lockedLead}
                  onChange={(value: unknown, option: unknown) => {
                    // A meeting belongs to one record. Without this, choosing the
                    // project first and the lead second still pairs them. Only
                    // relevant when both fields are actually offered — a
                    // lead-only/project-only type already hides the other one.
                    if (showProjectField && optionValue(value)) {
                      eventForm.setFieldValue("project", undefined)
                    }
                    offerAddress(option)
                  }}
                />
              </Form.Item>
            )}

            {showProjectField && (
              <Form.Item
                label="Link to Project"
                name="project"
                className={"mb-0 min-w-0"}
              >
                <CalendarAddProjectsSelect
                  onChange={(_: unknown, option: unknown) =>
                    offerAddress(option)
                  }
                />
              </Form.Item>
            )}

            {showBranchField && (
              <Form.Item
                label="Branch"
                name="team"
                className={"mb-0 min-w-0 sm:col-span-2"}
                rules={[
                  { required: true, message: "Select a branch for this event" }
                ]}
                extra="Not linked to a Lead or Project — choose which branch this event belongs to."
              >
                <Select
                  placeholder="Select a branch"
                  options={branchCandidates.map((b) => ({
                    value: b.id,
                    label: b.name
                  }))}
                />
              </Form.Item>
            )}
          </div>
        </CalendarFormSection>

        <CalendarFormSection
          icon={<EnvironmentOutlined />}
          title={"Location & Meeting"}
        >
          <div className={"grid grid-cols-1 gap-5 sm:grid-cols-2"}>
            <Form.Item
              label="Location"
              name="address"
              className={"mb-0 min-w-0"}
              extra="Taken from the linked record's property address, or its customer's. Edit it for a different location."
            >
              <AddressAutoComplete
                allowClear
                size="large"
                placeholder="Street, city, state, ZIP"
                onSelect={(suggestion) => {
                  // The picker's own onChange emits the street line only; a meeting
                  // Location is one free-text field, so keep the whole address.
                  const full =
                    suggestion.address.display || suggestion.address.street
                  eventForm.setFieldValue("address", full)
                  // A hand-picked address is the user's, not one this form offered.
                  offeredAddress.current = ""
                }}
              />
            </Form.Item>

            <Form.Item
              label="Meeting link"
              name="meeting_link"
              className={"mb-0 min-w-0"}
              rules={[
                { type: "url", message: "Enter a valid link (https://…)" }
              ]}
            >
              <TextInput
                disableAutoCapitalize
                allowClear
                placeholder="https://zoom.us/j/…  (optional)"
                prefix={<VideoCameraOutlined />}
                size="large"
                type="url"
              />
            </Form.Item>
          </div>
        </CalendarFormSection>

        <CalendarFormSection icon={<TeamOutlined />} title={"Participants"}>
          <div
            className={
              "grid grid-cols-1 items-start gap-5 sm:grid-cols-2 lg:grid-cols-[7fr_7fr_6fr]"
            }
          >
            <Form.Item
              label="Internal Participants (Team)"
              name="participants"
              className={"mb-0 min-w-0"}
            >
              <CalendarAddParticipantsSelect />
            </Form.Item>

            <div className={"min-w-0"}>
              <CalendarAddExternalParticipantsFormItem />
            </div>

            <div className={"min-w-0"}>
              <Form.Item
                name="invite_customer"
                valuePropName="checked"
                className={"mb-0 min-w-0"}
              >
                <Checkbox>
                  <span className={"font-medium"}>Invite Customer</span>
                </Checkbox>
              </Form.Item>
              <Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
                Send calendar invitation with limited meeting details.
              </Text>
            </div>
          </div>

          {inviteCustomer && (
            <Form.Item
              label="Customer Invitation Email"
              name="customer_invite_email"
              className={"mb-0 min-w-0"}
              extra="Prefilled from the linked lead or project's customer when known — edit or clear to override. Left blank, that customer's email is used."
              rules={[
                { type: "email", message: "Enter a valid customer email" }
              ]}
            >
              <TextInput
                disableAutoCapitalize
                type="email"
                placeholder="Use linked customer email when blank"
                size="large"
              />
            </Form.Item>
          )}

          {showCustomerUpload && (
            <div className={"min-w-0"}>
              <Form.Item
                name="customer_upload_enabled"
                valuePropName="checked"
                className={"mb-0 min-w-0"}
              >
                <Checkbox>
                  <span className={"font-medium"}>
                    Allow customer to upload documents
                  </span>
                </Checkbox>
              </Form.Item>
              <Text type={"secondary"} className={"mt-1 block pl-6 text-xs"}>
                Adds an upload link to the invitation — up to 10 files, 10 MB
                each. Files are saved in the lead&apos;s &quot;From
                Customer&quot; folder.
              </Text>
              {uploadWillReinvite && (
                <Text type={"warning"} className={"mt-1 block pl-6 text-xs"}>
                  The customer will receive an updated invitation with the
                  upload link.
                </Text>
              )}
            </div>
          )}

          {showProjectEducation && leadId && (
            <ProjectEducationInviteSection
              leadId={String(leadId)}
              isEdit={isEdit}
              inviteCustomer={Boolean(inviteCustomer)}
              showCustomerSharing={featureProjectEducationCustomerSharing}
              eventProjectEducation={
                isEdit
                  ? (initialValues?.project_education as Event["project_education"])
                  : undefined
              }
            />
          )}
        </CalendarFormSection>

        <CalendarFormSection
          icon={<PaperClipOutlined />}
          title={"Additional Information"}
        >
          <div className={"grid grid-cols-1 gap-5 sm:grid-cols-2"}>
            <div className={"flex min-w-0 flex-col gap-3"}>
              <Form.Item
                label="Attachments"
                name="upload"
                className={"mb-0 min-w-0"}
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value.length > availableCountFiles) {
                        return Promise.reject(
                          new Error("You can upload a maximum of 20 files.")
                        )
                      }
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <FileUpload
                  uploadProps={uploadProps}
                  files={uploadedFiles}
                  options={{
                    extensions: ALLOWED_FILE_EXTENSIONS,
                    maxSize: MAX_SIZE_MB
                  }}
                />
              </Form.Item>

              {!!attachments?.length && (
                <AttachmentsList
                  attachments={attachments}
                  preview={true}
                  onDelete={onDeletedFile}
                />
              )}
            </div>

            <Form.Item label="Notes" name="comment" className={"mb-0 min-w-0"}>
              <TextArea
                placeholder="Add any additional details..."
                rows={8}
                size="large"
                className={"h-full"}
              />
            </Form.Item>
          </div>
        </CalendarFormSection>
      </CalendarFormScrollArea>

      <div className="mt-4 flex shrink-0 items-center justify-between border-t border-gray-200 pt-4">
        {isEdit && onDelete ? (
          <Button
            disabled={loading}
            size="large"
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
          >
            Delete Event
          </Button>
        ) : (
          <span />
        )}
        <div className={"flex gap-3"}>
          <Button disabled={loading} size="large" onClick={handleModalCancel}>
            Cancel
          </Button>
          <Button
            loading={loading}
            size="large"
            type="primary"
            htmlType="submit"
          >
            {submitLabel ??
              (isEdit
                ? loading
                  ? "Saving Changes..."
                  : "Save Event"
                : loading
                  ? "Creating Event..."
                  : "Create Event")}
          </Button>
        </div>
      </div>
    </Form>
  )
}

export type { CalendarEventFormSubmission }
